import { AffiliationQueryCategoryEnum } from "@/enums/aem";
import { StudentOmrStatusEnum } from "@/enums/conductTest";
import { StudyMaterialTypeIdsEnum } from "@/enums/content";
import { DrawModeTypeEnum, ImplementationScoreTabsEnum } from "@/enums/global";
import { StudentLiveClassCategoryEnum } from "@/enums/liveClass";
import { StudentOmrFilterPropsType } from "@/types/activity";
import { ReportCardProp } from "@/types/affiliation";
import { ComponentColorType } from "@/types/componentType";
import { IonSegmentItem, UserAlert } from "@/types/global";

export const MaxPdfDownloadTimeout = 30000;
export const PdfDownloadRetryInterval = 5000;
export const MaxUploadImageSizeMB = 2;

export const PingCacheDuration = 60000;
// Must stay in step with `build.target` in vite.config.js — that is the Chromium version the
// bundle is compiled for, and this is the version the failure screen tests the running browser
// against. Windows 7 can never go past Chrome 109, so those machines are permanently below it.
export const MIN_SUPPORTED_CHROME_VERSION = 111;
export const StatusBarCheckTimeout = 3000;
export const ExitAlertCount = 2;
export const BackButtonPriority = 10;
export const ScrollThreshold = 100;
export const AutoUpdateTimeout = 30000;
// Ceiling on consecutive automatic live-update attempts for one target version, counted in
// persisted state because each attempt can restart the app and wipe everything that is not.
// Mirrors MAX_RELOAD_ATTEMPTS in appSettingsStore: past the cap the app stops restarting itself
// and hands over to ModalAppUpdate, which the user can act on or dismiss.
export const MAX_LIVE_UPDATE_ATTEMPTS = 3;

export const VimeoBaseUrl = "https://player.vimeo.com/video/";
export const VimeoVideoBaseUrl = "https://vimeo.com/";
export const VimeoOembedUrl = "https://vimeo.com/api/oembed.json";
export const VimeoThumbnailWidth = 640;

export const YoutubeBaseUrl = "https://www.youtube.com/embed/";
export const YOUTUBE_WATCH_BASE_URL = "https://www.youtube.com/watch?v=";
export const YoutubeThumbnailBaseUrl = "https://img.youtube.com/vi/";
// Every YouTube video id is exactly 11 characters — the length check is what stops a
// non-YouTube url whose tail happens to match the regex from being treated as one.
export const YOUTUBE_VIDEO_ID_LENGTH = 11;
export const YoutubeQueryParams =
  "amp;iv_load_policy=3&amp;modestbranding=1&amp;playsinline=1&amp;showinfo=0&amp;rel=0&amp;enablejsapi=1";
// Matches every YouTube URL shape the backend hands us and captures the 11-char video id
// in group 1. `live/` and `shorts/` are here because live-class recordings come back as
// https://youtube.com/live/<id>?feature=share — without them the url falls through to the
// Vimeo branch of ModalVideoPlayer and @vimeo/player throws "is not a vimeo.com url".
// The alternation is non-capturing so the id stays at group 1 when a shape is added.
export const YoutubeUrlRegex =
  /^.*(?:(?:youtu\.be\/)|(?:v\/)|(?:\/u\/\w\/)|(?:embed\/)|(?:live\/)|(?:shorts\/)|(?:watch\?))\??v?=?([^#&?]*).*/;

export const DifficultyColor: { [DifficultyLevel: string]: ComponentColorType } = Object.freeze({
  Easy: "success",
  Medium: "warning",
  Hard: "danger",
});

export const Breakpoints = {
  xxxs: 359,
  xxs: 420,
  xs: 575,
  s: 767,
  m: 991,
  l: 1199,
  xl: 1399,
};

export const COLOR_PALETTE = [
  "primary",
  "secondary",
  "success",
  "tertiary",
  "warning",
  "text",
  "danger",
  "light",
  "medium",
  "dark",
] as const;

export const ISODateFormat = "yyyy-MM-dd'T'HH:mm:ss.SSS";

export const DefaultNewPassword = "abc123";

export const ProgressThresholds = [20, 40, 60, 80, 100];
export const ProgressColors = ["#B03E3E", "#FF835C", "#FFBF44", "#8BC32E", "#16A085"];
export const DefaultProgressColor = "#B03E3E";

// Parallel severity-name array for class-based progress-bar fills. Indices match
// ProgressThresholds / ProgressColors. Consumers use the `-dark` variant class
// to get the saturated fill: "bg-color-<name>-dark". The base
// ".bg-color-<name>" alone renders a near-white background — the `-dark` suffix
// opts into the fully-saturated color (see src/assets/styles/colors.scss).
//
// Palette substitution note (Wave 1 Task 0.4): the plan originally proposed
// red/orange/yellow/lime/green, but the project palette only defines semantic
// names (danger/warning/success/text/medium + primary/secondary/tertiary).
// Substituted to the closest severity mapping. Trade-off: the 5-step hex ramp
// collapses to 3 distinct classes (danger/warning/success), with duplicate
// classes at the 20→40 and 60→80 threshold boundaries. Callers needing the
// original 5-distinct-hue gradient should use ProgressColors + inline :style
// instead of this name array.
export const ProgressColorNames: ReadonlyArray<string> = Object.freeze([
  "danger", // [0, 20]   — critical (was #B03E3E)
  "warning", // [20, 40]  — poor (was #FF835C, no palette match; using warning)
  "warning", // [40, 60]  — neutral (was #FFBF44)
  "success", // [60, 80]  — good (was #8BC32E, no palette match; using success)
  "success", // [80, 100] — excellent (was #16A085)
]);
export const DefaultProgressColorName = "danger";

export const MultiSubjectName = "Multi-Subject";
export const DefaultSubjectIcon = "default";
export const AddBulkUsersCsvTemplateUrl = "~/webapp/AddBulkUsersTemplate.csv";
export const HeightFabButton = "60px";
export const HeightPageTitleContainer = "30px";

/**
 * Separator embedded in live-update bundle IDs to stamp the minimum native
 * binary version the bundle was published for.
 *
 * Format: `${AppVersion}${LiveBundleIdSeparator}${MinNativeVersion}`
 *   e.g. "3.14.2__native-3.14.0"
 *
 * Legacy bundles published before this stamp existed have no separator and
 * are treated as "unknown min-native" by parseLiveBundleId (in appUtils.ts).
 */
export const LiveBundleIdSeparator = "__native-";

export const appRefreshAlert = {
  messageTitle: "Update available",
  messageText: "You're running an older version. Refresh the page (Ctrl + F5) to get the latest.",
  alertColor: "warning",
  alertIcon: "refresh-alert",
} as UserAlert;

export const VueMathJaxOptions = {
  extensions: ["tex2jax.js"],
  showProcessingMessages: false,
  jax: ["input/TeX", "output/CommonHTML"],
  showMathMenu: false,
  tex2jax: {
    inlineMath: [
      ["$", "$"],
      ["\\(", "\\)"],
    ],
    displayMath: [
      ["$$", "$$"],
      ["\\[", "\\]"],
    ],
    processEscapes: false,
    processClass: "tex2jax_process",
  },
  CommonHTML: {
    scale: 85,
    mtextFontInherit: true,
    linebreaks: { automatic: true },
  },
};

export const MaxAffiliationActivityDays = 365;
export const PixelErrorThreshold = 10;
export const OptionsArray = ["A", "B", "C", "D"];

export const MaxManagePreloadedSelections = 5;

export const MaxPracticeChapters = 3;

export const ActionableCardProps: {
  [DifficultyLevel: string]: {
    buttonName: string;
    buttonIcon: string;
    cardColor: ComponentColorType;
    customMessage: string;
    actionRouteName: string;
  };
} = Object.freeze({
  Learn: {
    buttonName: "Watch Video",
    buttonIcon: "camera",
    cardColor: "danger",
    customMessage: "Watch All Videos",
    actionRouteName: "LearnConceptsVideos",
  },
  SelfPractice: {
    buttonName: "Start Practice",
    buttonIcon: "player-play",
    cardColor: "secondary",
    customMessage: " Start a Practice Session",
    actionRouteName: "SelfPractice",
  },
  SelfAssignment: {
    buttonName: "Start Assignment",
    buttonIcon: "player-play",
    cardColor: "success",
    customMessage: " Start a Self Assignment",
    actionRouteName: "SelfAssignment",
  },
  SelfTest: {
    buttonName: "Start Test",
    buttonIcon: "stopwatch",
    cardColor: "tertiary",
    customMessage: "Start Your Own Test",
    actionRouteName: "SelfTests",
  },
  InstituteTest: {
    buttonName: "Start Test",
    buttonIcon: "hourglass",
    cardColor: "primary",
    customMessage: "Start an Institute Test",
    actionRouteName: "InstituteTests",
  },
  Assignment: {
    buttonName: "Start Assignment",
    buttonIcon: "edit",
    cardColor: "success",
    customMessage: "Start an Assignment",
    actionRouteName: "Assignments",
  },
  Concepts: {
    buttonName: "View Concepts",
    buttonIcon: "notebook",
    cardColor: "secondary",
    customMessage: "Revise Concepts",
    actionRouteName: "LearnConceptsVideos",
  },
  ScheduleClass: {
    buttonName: "Schedule Class",
    buttonIcon: "users",
    cardColor: "warning",
    customMessage: "Schedule a Class",
    actionRouteName: "CreateLiveClass",
  },
  CreateAssignment: {
    buttonName: "Create Assignment",
    buttonIcon: "edit",
    cardColor: "success",
    customMessage: "Create an Assignment",
    actionRouteName: "ManageAssignments",
  },
  CreateTest: {
    buttonName: "Create Test",
    buttonIcon: "hourglass",
    cardColor: "primary",
    customMessage: "Create a Test",
    actionRouteName: "ManageInstituteTests",
  },
});

export const PracticeSessionCardProps: {
  [key: string]: {
    buttonName: string;
    buttonIcon: string;
    cardColor: ComponentColorType;
    showMetrics: boolean;
  };
} = Object.freeze({
  Paused: {
    buttonName: "Resume",
    buttonIcon: "player-play",
    cardColor: "success",
    showMetrics: false,
  },
  Completed: {
    buttonName: "View Report",
    buttonIcon: "clipboard-data",
    cardColor: "medium",
    showMetrics: true,
  },
});

export const StudentLiveClassCardProps: {
  [LiveClassCategory: number]: string;
} = Object.freeze({
  [StudentLiveClassCategoryEnum.SCHEDULED]: "secondary",
  [StudentLiveClassCategoryEnum.ACTIVE]: "success",
  [StudentLiveClassCategoryEnum.COMPLETED]: "medium",
});

export const SubjectCardButtonProps = Object.freeze({
  Learn: {
    buttonName: "Learn",
    buttonIcon: "bulb",
    actionRouteName: "LearnConceptsVideos",
  },
  Practice: {
    buttonName: "Practice",
    buttonIcon: "edit",
    actionRouteName: "SelfPractice",
  },
  SelfTest: {
    buttonName: "Self Test",
    buttonIcon: "alarm",
    actionRouteName: "SelfTests",
  },
  SubjectCoverage: {
    buttonName: "Subject Coverage",
    buttonIcon: "clipboard-data",
    actionRouteName: "ReportSubjectCoverage",
  },
});

export const ReportCardProps: { [reportType: string]: ReportCardProp } = Object.freeze({
  StudentProgressReport: {
    cardName: "Student Progress",
    cardColor: "tertiary",
    iconName: "trend",
    reportDescription:
      "A detailed student progress report analyzing institute test performance and chapter-wise.",
    routeName: "ReportStudentProgress",
  },
  EffortAnalysis: {
    cardName: "Effort Analysis",
    cardColor: "secondary",
    iconName: "metrics-performance",
    reportDescription:
      "Analyze the performance and effort of each student over the last few weeks.",
    routeName: "ReportBatchEffortAnalysis",
  },
  BatchPerformance: {
    cardName: "Batch Performance",
    cardColor: "success",
    iconName: "medal",
    reportDescription:
      "Review an entire batch's progress with student-wise reports and tailored recommendations.",
    routeName: "ReportBatchPerformance",
  },
  SubjectCoverage: {
    cardName: "Syllabus Coverage",
    cardColor: "primary",
    iconName: "open-book",
    reportDescription: "Analyze the chapter-wise coverage of a batch for any subject.",
    routeName: "ReportSyllabusCoverage",
  },
  InstituteTestAnalysis: {
    cardName: "Test Analysis",
    cardColor: "tertiary",
    iconName: "percentage",
    reportDescription:
      "Detailed results and analysis of students' performance in the institute test.",
    routeName: "ReportBatchTestAnalysis",
  },
});

export const StudyMaterialButtonProps = Object.freeze({
  [StudyMaterialTypeIdsEnum.GENERAL]: {
    studyMaterialType: "General",
    buttonIcon: "plus-minus",
    cardColor: "success",
  },
  [StudyMaterialTypeIdsEnum.NOTES]: {
    studyMaterialType: "Notes",
    buttonIcon: "edit",
    cardColor: "success",
  },
  [StudyMaterialTypeIdsEnum.IMPORTANT_QUESTIONS]: {
    studyMaterialType: "Important Questions",
    buttonIcon: "message-circle",
    cardColor: "success",
  },
  [StudyMaterialTypeIdsEnum.SOLVED_EXAMPLES]: {
    studyMaterialType: "Solved Examples",
    buttonIcon: "key",
    cardColor: "success",
  },
  [StudyMaterialTypeIdsEnum.WORKSHEETS]: {
    studyMaterialType: "Worksheets",
    buttonIcon: "list-details",
    cardColor: "success",
  },
  [StudyMaterialTypeIdsEnum.NCERT_SOLUTIONS]: {
    studyMaterialType: "NCERT Solutions",
    buttonIcon: "notebook",
    cardColor: "success",
  },
});

export const StudentOmrFilterProps: { [filter: string]: StudentOmrFilterPropsType } = Object.freeze(
  {
    [StudentOmrStatusEnum.PENDING]: { value: "Pending", color: "medium", icon: "plus" },
    [StudentOmrStatusEnum.UPLOADED]: { value: "Uploaded", color: "tertiary", icon: "check" },
    [StudentOmrStatusEnum.APPROVED]: { value: "Approved", color: "success", icon: "checks" },
    [StudentOmrStatusEnum.REVIEW]: { value: "Review", color: "secondary", icon: "eye" },
    [StudentOmrStatusEnum.DUPLICATE]: { value: "Duplicate", color: "danger", icon: "copy" },
    [StudentOmrStatusEnum.ABSENT]: { value: "Absent", color: "warning", icon: "question-mark" },
  },
);

export const QueryCategoryDefaultDeadlines = Object.freeze({
  [AffiliationQueryCategoryEnum.TECH_RELATED]: 7,
  [AffiliationQueryCategoryEnum.CURRICULUM_AND_PEDAGOGY]: 3,
  [AffiliationQueryCategoryEnum.INFRA_RELATED]: 7,
  [AffiliationQueryCategoryEnum.ASM_RELATED]: 2,
  [AffiliationQueryCategoryEnum.DISPATCH_RELATED]: 3,
  [AffiliationQueryCategoryEnum.AFFILIATION_EXPECTATION]: 7,
  [AffiliationQueryCategoryEnum.PEOPLE_ALIGNMENT]: 3,
  [AffiliationQueryCategoryEnum.PROCESS_ALIGNMENT]: 7,
  [AffiliationQueryCategoryEnum.PROGRAM_EXPECTATION]: 3,
  [AffiliationQueryCategoryEnum.OTHER]: 7,
});

export const CanvasDrawModeButtons = [
  {
    mode: DrawModeTypeEnum.DRAW,
    iconName: "pencil",
    tooltipText: "Pen",
  },
  {
    mode: DrawModeTypeEnum.RECTANGLE,
    iconName: "square",
    tooltipText: "Box",
  },
  {
    mode: DrawModeTypeEnum.CIRCLE,
    iconName: "circle",
    tooltipText: "Circle",
  },
  {
    mode: DrawModeTypeEnum.NOTE,
    iconName: "text-plus",
    tooltipText: "Note",
  },
  {
    mode: DrawModeTypeEnum.ERASER,
    iconName: "eraser",
    tooltipText: "Erase",
  },
];

export const DrawColorOptions = ["red", "blue", "black"];

// The swatch names above are handed to fabric as raw CSS colors; these are the themed text-color
// utilities the toolbar paints the matching swatch icon with.
export const DRAW_COLOR_CLASSES: { [drawColor: string]: string } = {
  red: "color-danger",
  blue: "color-tertiary-dark",
  black: "color-dark",
};

export const BASIC_USAGE_COLORS: { [usageLabel: string]: ComponentColorType } = {
  None: "danger",
  Low: "danger",
  Medium: "warning",
  High: "success",
};

export const TEACHER_USAGE_LABELS = ["None", "Low", "Medium", "High"];
export const STUDENT_USAGE_LABELS = ["Low", "Medium", "High"];
export const SCHOOL_USAGE_COLORS: { [teacherLabel: string]: { [studentLabel: string]: string } } = {
  None: { Low: "danger", Medium: "danger", High: "danger" },
  Low: { Low: "danger", Medium: "warning", High: "warning" },
  Medium: { Low: "danger", Medium: "warning", High: "success" },
  High: { Low: "warning", Medium: "success", High: "success" },
};

export const QUESTION_PAPER_TEMPLATE_URL =
  "offlineinstitutetest/assetTemplates/[[TEMPLATE_NAME]]_TEMPLATE.jpg";

export const ImplementationScoreTabsProps: Array<IonSegmentItem> = [
  {
    Name: ImplementationScoreTabsEnum.TEACHER_USAGE,
    Value: ImplementationScoreTabsEnum.TEACHER_USAGE,
    Icon: "user",
  },
  {
    Name: ImplementationScoreTabsEnum.STUDENT_USAGE,
    Value: ImplementationScoreTabsEnum.STUDENT_USAGE,
    Icon: "users",
  },
];

export const VideoPlayerVolumeIconMap: Record<number, string> = {
  [0]: "volume-off",
  [0.3]: "volume",
  [0.7]: "volume",
  [1]: "volume-2",
};
export const VideoPlayerPlaybackRates = [0.5, 1, 1.5, 2];

// Default copy for ModalAddBulkUsersSummary. Extracted so the modal's props can default to the
// exact strings it has always rendered — the admin bulk-add call site must stay unchanged when
// the new optional props are absent.
export const DEFAULT_BULK_SUMMARY_TITLE =
  "Here is a summary of the file you submitted for bulk registration. Details of any rows that failed registration are provided below.";
export const DEFAULT_BULK_SUMMARY_SUCCESS_LABEL = "Registered Successfully";
